import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Image as ImageIcon, FileText, Download, Link2 } from "lucide-react";
import { useMemo, useState } from "react";
import ImageLightbox from "@/components/ImageLightbox";

export default function MediaPanel({ open, onOpenChange, items, fileUrlFor, titleName }) {
  const [tab, setTab] = useState("photos");
  const [viewerIdx, setViewerIdx] = useState(-1);

  const { photos, docs, links } = useMemo(() => {
    const photos = [];
    const docs = [];
    const links = [];
    for (const it of items) {
      if (it.type === "file") {
        const isImg = it.is_image || (it.mime_type || "").startsWith("image/");
        (isImg ? photos : docs).push(it);
      } else if (it.type === "link" && it.url) {
        links.push(it);
      }
    }
    // newest first
    photos.reverse();
    docs.reverse();
    links.reverse();
    return { photos, docs, links };
  }, [items]);

  const tabBtn = (id, label, count) => (
    <button
      key={id}
      data-testid={`media-tab-${id}`}
      onClick={() => setTab(id)}
      className={`flex-1 text-xs font-medium py-2 rounded-lg transition-all ${
        tab === id
          ? "bg-[#1E1E1E] text-[#F3F4F6]"
          : "text-[#9CA3AF] hover:text-[#F3F4F6]"
      }`}
    >
      {label} <span className="text-[10px] text-[#6B7280]">({count})</span>
    </button>
  );

  return (
    <>
    <Sheet
      open={open}
      onOpenChange={(next) => {
        // Esc / outside-click while the photo viewer is up should only close
        // the viewer, not the whole panel behind it.
        if (!next && viewerIdx >= 0) return;
        onOpenChange(next);
      }}
    >
      <SheetContent
        data-testid="media-panel"
        side="right"
        className="bg-[#0F0F11] border-l border-[#1E1E1E] text-[#F3F4F6] p-0 w-full sm:max-w-md flex flex-col"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-[#1E1E1E]">
          <SheetTitle className="font-heading text-base text-[#F3F4F6]">
            Shared media
          </SheetTitle>
          <div className="text-xs text-[#6B7280] truncate">{titleName}</div>
        </SheetHeader>

        <div className="px-4 pt-3">
          <div className="flex gap-1 p-1 rounded-xl bg-[#121212] border border-[#1E1E1E]">
            {tabBtn("photos", "Photos", photos.length)}
            {tabBtn("files", "Files", docs.length)}
            {tabBtn("links", "Links", links.length)}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
          {tab === "photos" && (
            photos.length === 0 ? (
              <EmptyHint icon={<ImageIcon className="h-6 w-6" />} text="No photos yet in this thread." />
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {photos.map((it, i) => (
                  <button
                    type="button"
                    key={it.item_id}
                    onClick={() => setViewerIdx(i)}
                    data-testid={`media-photo-${it.item_id}`}
                    className="aspect-square rounded-lg overflow-hidden bg-[#1E1E1E] border border-[#27272A] hover:border-[#3B82F6]/60 transition-all"
                    title={it.file_name}
                    aria-label={`Open ${it.file_name || "photo"}`}
                  >
                    <img
                      src={fileUrlFor(it.item_id)}
                      alt={it.file_name || "photo"}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )
          )}

          {tab === "files" && (
            docs.length === 0 ? (
              <EmptyHint icon={<FileText className="h-6 w-6" />} text="No files yet in this thread." />
            ) : (
              <ul className="space-y-2">
                {docs.map((it) => (
                  <li key={it.item_id}>
                    <a
                      href={fileUrlFor(it.item_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={it.file_name || undefined}
                      data-testid={`media-file-${it.item_id}`}
                      className="flex items-center gap-3 p-3 rounded-xl bg-[#121212] border border-[#1E1E1E] hover:border-[#3B82F6]/60 transition-all"
                    >
                      <div className="h-10 w-10 rounded-lg bg-[#1E1E1E] border border-[#27272A] flex items-center justify-center text-[#3B82F6] shrink-0">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#F3F4F6] truncate">
                          {it.file_name || "file"}
                        </div>
                        <div className="text-[11px] text-[#9CA3AF] truncate">
                          {(it.mime_type || "file")}{it.size ? ` · ${(it.size / 1024).toFixed(1)} KB` : ""}
                        </div>
                      </div>
                      <Download className="h-4 w-4 text-[#9CA3AF] shrink-0" />
                    </a>
                  </li>
                ))}
              </ul>
            )
          )}

          {tab === "links" && (
            links.length === 0 ? (
              <EmptyHint icon={<Link2 className="h-6 w-6" />} text="No links shared in this thread." />
            ) : (
              <ul className="space-y-2">
                {links.map((it) => {
                  const p = it.preview || {};
                  let host = p.site;
                  if (!host) { try { host = new URL(it.url).hostname; } catch { host = ""; } }
                  return (
                    <li key={it.item_id}>
                      <a
                        href={it.url}
                        target="_blank"
                        rel="noopener noreferrer external"
                        data-testid={`media-link-${it.item_id}`}
                        className="flex items-center gap-3 p-3 rounded-xl bg-[#121212] border border-[#1E1E1E] hover:border-[#3B82F6]/60 transition-all"
                      >
                        {p.image ? (
                          <img src={p.image} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" />
                        ) : (
                          <div className="h-12 w-12 rounded-lg bg-[#1E1E1E] border border-[#27272A] flex items-center justify-center text-[#3B82F6] shrink-0">
                            <Link2 className="h-5 w-5" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-[#F3F4F6] truncate">
                            {p.title || it.url}
                          </div>
                          <div className="text-[11px] text-[#3B82F6] truncate">{host}</div>
                        </div>
                      </a>
                    </li>
                  );
                })}
              </ul>
            )
          )}
        </div>
      </SheetContent>
    </Sheet>
      <ImageLightbox
        open={viewerIdx >= 0}
        images={photos.map((p) => ({
          src: fileUrlFor(p.item_id),
          alt: p.file_name || "photo",
          downloadName: p.file_name,
        }))}
        index={viewerIdx < 0 ? 0 : viewerIdx}
        onClose={() => setViewerIdx(-1)}
      />
    </>
  );
}

function EmptyHint({ icon, text }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 gap-3 text-[#6B7280]">
      <div className="h-10 w-10 rounded-full bg-[#1E1E1E] border border-[#27272A] flex items-center justify-center text-[#3B82F6]">
        {icon}
      </div>
      <div className="text-xs">{text}</div>
    </div>
  );
}
